/** Templates of a `NavPage` record. */
export const NAV_PAGE_TEMPLATES = ["landing", "record"] as const;
export type NavPageTemplate = (typeof NAV_PAGE_TEMPLATES)[number];

/** Position of a `NavPageSlot` on its page. */
export const NAV_SLOT_REGIONS = ["body", "tab", "rail"] as const;
export type NavSlotRegion = (typeof NAV_SLOT_REGIONS)[number];

/** Content of a `NavPageSlot`. */
export const NAV_SLOT_TYPES = [
  "collection",
  "properties",
  "compose",
  "custom",
  "facet-chips",
  "text",
] as const;
export type NavSlotType = (typeof NAV_SLOT_TYPES)[number];

/** How a slot is scoped to a parent record. */
export const NAV_BIND_SOURCES = ["none", "page", "slot"] as const;
export type NavBindSource = (typeof NAV_BIND_SOURCES)[number];

export const NAV_BIND_EDGE_ON = ["target", "parent"] as const;
export type NavBindEdgeOn = (typeof NAV_BIND_EDGE_ON)[number];

/** Edge direction for one `bindPath` hop (CPES-16937). Omitted means outgoing. */
export const NAV_BIND_PATH_DIRECTIONS = ["outgoing", "incoming"] as const;
export type NavBindPathDirection = (typeof NAV_BIND_PATH_DIRECTIONS)[number];

/**
 * One hop on `NavPageSlot.bindPath`. Last hop must equal `targetSchema`.
 * `version` defaults to `1.0.0` when the seed omits it.
 */
export interface NavBindPathHop {
  resource: string;
  version: string;
  /**
   * Edge direction this hop follows. Omitted means outgoing, the record-service default.
   * `incoming` walks an edge that points at the current record.
   */
  direction?: NavBindPathDirection;
}

export const NAV_TEXT_TONES = ["default", "muted"] as const;
export type NavTextTone = (typeof NAV_TEXT_TONES)[number];

/** Landing format authored on a list slot. Not a named-view format. */
export const SLOT_LANDING_FORMATS = ["table", "gallery"] as const;
export type SlotLandingFormat = (typeof SLOT_LANDING_FORMATS)[number];

/**
 * One perspective of a compose slot or of a page: the same blocks, each bound
 * block showing another named view. `bindings` maps a slot key of the compose
 * slot's `childPage` (or of the page's own slots) to a view name.
 */
export interface NavSlotVariant {
  key: string;
  label: string;
  isDefault?: boolean;
  bindings: Record<string, string>;
}

/** The one part of a named properties view a properties slot shows. */
export type NavSlotPart = "media" | "composition" | `sec:${string}`;

/**
 * A `NavPage` record as stored on the BE. Joined to slots by business `key`,
 * never the server `id`.
 */
export interface NavPageRecord {
  key: string;
  label: string;
  template: NavPageTemplate;
  targetSchema?: string;
  targetVersion?: string;
  isDefault?: boolean;
  /** Whether the record header offers lifecycle transitions or only shows status. */
  lifecycleControls?: "interactive" | "status-only";
  permission?: string;
  enabled?: boolean;
  description?: string;
  /** Perspectives over the page's own blocks, switched by one control in the page toolbar row. */
  variants?: NavSlotVariant[];
}

/**
 * A `NavPageSlot` record as stored on the BE. Three orthogonal axes: position
 * (`region` + `order`), content (`type` + view / child / component), scope
 * (`bindSource` + edge).
 */
export interface NavPageSlotRecord {
  key: string;
  page: string;
  region: NavSlotRegion;
  order: number;
  type: NavSlotType;
  label?: string;
  description?: string;
  slug?: string;
  group?: string;
  targetSchema?: string;
  targetVersion?: string;
  view?: string;
  /** Default table/gallery when the page loads. Runtime switch can still change it. */
  format?: SlotLandingFormat;
  /** Record page a row of this list opens. */
  detailPage?: string;
  /**
   * Properties bound to a selected row. `record` draws that row as a record
   * overview and the driving list steps aside. Absent keeps the field grid.
   */
  detailChrome?: "record";
  childPage?: string;
  childPages?: string[];
  /** Compose only: perspectives over `childPage`, switched by one control above it. */
  variants?: NavSlotVariant[];
  component?: string;
  bindSource?: NavBindSource;
  bindSourceSlot?: string;
  bindRelationship?: string;
  /**
   * Hops from the bound parent to `targetSchema`. Collection + `bindEdgeOn: parent`.
   * Two or more hops use the reachable route. The last resource is the child.
   * Each hop may set `direction`. Alternative to `bindRelationship`.
   */
  bindPath?: NavBindPathHop[];
  bindEdgeOn?: NavBindEdgeOn;
  bindField?: string;
  bindParentKey?: string;
  bindFacetSlot?: string;
  emitsSelection?: boolean;
  /**
   * Collection only: this list is where records of `targetSchema` live, so a link to one opens
   * here. Usually inferred from ownership; set to settle a type two lists reach.
   */
  isRecordHome?: boolean;
  showCount?: boolean;
  /**
   * When true, the block's toolbar offers its Views selector (and a list's Table/Gallery switch).
   * Default off: unset hides it, as the schema's `"default": false` says.
   */
  allowViewSwitch?: boolean;
  /** Properties only: show just this part of `view` instead of the whole view. */
  part?: NavSlotPart;
  /**
   * When false, never render the slot title. Unscoped landing collections
   * also default off (`showHeading: true` to force the title).
   */
  showHeading?: boolean;
  field?: string;
  textValue?: string;
  tone?: NavTextTone;
  permission?: string;
  enabled?: boolean;
}

/** Why a slot is dropped at runtime (fail closed). */
export type SlotIssueCode =
  | "missing-schema"
  | "missing-view-name"
  | "missing-child-page"
  | "compose-cycle"
  | "missing-component"
  | "missing-bind-source-slot"
  | "missing-facet-field"
  | "missing-text";

export interface SlotIssue {
  code: SlotIssueCode;
  slotKey: string;
}

export interface ResolvedSlot {
  slot: NavPageSlotRecord;
  /** Default compose child (`childPage`). */
  child?: PageModel;
  /** Compose inner tabs (`childPages`), default page first. */
  children?: PageModel[];
  issues: SlotIssue[];
}

/** One page with its slots joined, sorted, and compose children resolved. */
export interface PageModel {
  page: NavPageRecord;
  body: ResolvedSlot[];
  tabs: ResolvedSlot[];
  rail: ResolvedSlot[];
}
